import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * About page - Information about Gardeno Games and the Deck Visualizer tool
 * shadcn/ui version
 */
export default function AboutShadcn() {
  const { t } = useLanguage();

  return (
    <div className="min-h-[calc(100vh-48px-40px)] bg-gradient-to-b from-background to-muted/30">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-brand to-brand-accent text-white">
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold tracking-widest uppercase border-2 border-white inline-block px-6 py-2 mb-6">
            {t('about.title') || 'About Gardeno Games'}
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-8">

      {/* Our Mission */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>{t('about.mission') || 'Our Mission'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="leading-relaxed">
            {t('about.missionText') || '2 friends sharing our love of games and collecting.'}
          </p>
          <p className="leading-relaxed font-medium">
            {t('about.missionGoal') || 'Our next goal is to represent team USA well at the 2026 Ultra League World Championship!'}
          </p>
        </CardContent>
      </Card>

      {/* Team Section */}
      <h2 className="text-2xl font-bold text-center mb-6">{t('about.team') || 'The Team'}</h2>
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Gankun */}
        <Card className="text-center">
          <CardContent className="pt-6">
            <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-muted bg-gradient-to-br from-red-100 to-white">
              <img
                src="/images/Mebius.PNG"
                alt={t('about.gankunName') || 'Gankun'}
                className="w-full h-full object-cover"
              />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('about.gankunName') || 'Gankun'}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('about.gankunDesc') || 'The Resident Ultraman Fan!'}
            </p>
          </CardContent>
        </Card>

        {/* DurianIRL */}
        <Card className="text-center">
          <CardContent className="pt-6">
            <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-muted bg-gradient-to-br from-red-100 to-black">
              <img
                src="/images/BelialGeed.PNG"
                alt={t('about.durianName') || 'DurianIRL'}
                className="w-full h-full object-cover"
              />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('about.durianName') || 'DurianIRL'}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('about.durianDesc') || 'The Team Card Game Expert!'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* What is this tool? */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('about.whatIsThis') || 'What is the Deck Builder?'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="leading-relaxed">
            {t('about.whatIsThisText') || 'The Deck Builder is a community tool designed to help players build, analyze, and share decks for the Ultraman Card Game.'}
          </p>
          <p className="leading-relaxed">
            {t('about.features') || 'Features include deck building, card search, statistical analysis, hand simulation, and export options for Tabletop Simulator.'}
          </p>
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('about.disclaimer') || 'Disclaimer'}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="leading-relaxed">
            {t('about.disclaimerText') || 'This is a fan-made, non-commercial project.'}
          </p>
        </CardContent>
      </Card>

      {/* Links */}
      <Card>
        <CardHeader>
          <CardTitle>{t('about.links') || 'Links'}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li>
              <a
                href="https://ultraman-cardgame.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline flex items-center gap-2"
              >
                <img
                  src="https://www.google.com/s2/favicons?domain=ultraman-cardgame.com&sz=64"
                  alt="Official Ultraman Card Game"
                  className="h-5 w-5 rounded-sm"
                />
                {t('about.officialSite') || 'Official Ultraman Card Game Website'}
              </a>
            </li>
            <li>
              <a
                href="https://x.com/DurianIrl"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline flex items-center gap-2"
              >
                <img
                  src="https://www.google.com/s2/favicons?domain=x.com&sz=64"
                  alt="X"
                  className="h-5 w-5 rounded-sm"
                />
                {t('about.xProfile') || 'X Profile (Gardeno Games)'}
              </a>
            </li>
            <li>
              <a
                href="https://www.youtube.com/@gardenogames"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline flex items-center gap-2"
              >
                <img
                  src="https://www.google.com/s2/favicons?domain=youtube.com&sz=64"
                  alt="YouTube"
                  className="h-5 w-5 rounded-sm"
                />
                {t('about.youtube') || 'YouTube (Gardeno Games)'}
              </a>
            </li>
            <li>
              <a
                href="https://www.patreon.com/gardenogames/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline flex items-center gap-2"
              >
                <img
                  src="https://www.google.com/s2/favicons?domain=patreon.com&sz=64"
                  alt="Patreon"
                  className="h-5 w-5 rounded-sm"
                />
                {t('about.patreon') || 'Patreon (Gardeno Games)'}
              </a>
            </li>
          </ul>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
